/**
 * Routing global de modelos LLM (Fase 33).
 *
 * Permite override de `(provider, model)` por task. Los combos se filtran
 * dinámicamente contra el catálogo central (`MODEL_CATALOG`) — los modelos
 * `experimental` muestran warning, los `deprecated` sólo aparecen si ya
 * estaban en uso. Los cambios surten efecto en la próxima sesión (no
 * mid-session) — mismo contrato que `module_config`.
 */
import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchRoutingAudit,
  fetchRoutingConfig,
  patchRoutingConfig,
  type ModelOverride,
  type RoutingAuditEntry,
  type RoutingConfigResponse,
  type RoutingOverrides,
  type TaskType,
} from '../../api/admin/routing.js';
import { AdminApiError } from '../../api/admin/_core.js';

// Pricing helper — formatea según el shape del modelo:
//   LLM    → "$3/$15 per 1M"   (input / output tokens)
//   TTS    → "$500/Mchar"      (chars)
//   STT    → "$0,40/hr"        (minutos)
function formatPricing(p: {
  inputPer1M?: number;
  outputPer1M?: number;
  perMChar?: number;
  perMinute?: number;
}): string {
  if (p.inputPer1M != null && p.outputPer1M != null) {
    return `$${p.inputPer1M}/$${p.outputPer1M} per 1M`;
  }
  if (p.perMChar != null) {
    return `$${p.perMChar}/Mchar`;
  }
  if (p.perMinute != null) {
    const perHour = (p.perMinute * 60).toFixed(2);
    return `$${perHour}/hr`;
  }
  return '—';
}

// Las tasks rutables desde el BO. Fase 33.5: tts/stt incorporados (override
// SOLO mismo provider — los WebSocket transports están acoplados al SDK).
// pronunciation/phone.call/voice/web_search siguen fuera por ahora.
const ROUTABLE_TASKS: readonly TaskType[] = [
  'llm.main',
  'llm.corrections',
  'llm.distill',
  'llm.radar',
  'llm.estimate',
  'llm.placement',
  'llm.skills',
  'llm.debrief',
  'llm.translate',
  // Split del cajón de sastre llm.translate (2026-07-17): cada feature con su
  // dial propio.
  'llm.explain',
  'llm.suggest',
  'llm.scenario',
  'llm.lookup',
  'llm.meaning',
  'llm.example_judge',
  'llm.game',
  'llm.game_check',
  'llm.game_solution',
  'llm.recall_select',
  'llm.recall',
  'llm.reflect',
  'llm.safety',
  'llm.summarize',
  'llm.theory',
  'llm.news_classify',
  'llm.verify',
  'video.analyze',
  'tts',
  'stt',
];

const TASK_DESCRIPTIONS: Partial<Record<TaskType, string>> = {
  'llm.main': 'LLM principal de cada turno',
  'llm.corrections': 'Side-runner de correcciones (languages dual-LLM)',
  'llm.distill': 'Post-sesión · extracción mecánica (vocab, perfil, glosas)',
  'llm.radar': 'Post-sesión · radar de errores y focos (speak)',
  'llm.estimate': 'Post-sesión · nivel CEFR de cada charla (Jev o LLM)',
  'llm.placement': 'Entrevista de nivel del onboarding (LLM: escribe el porqué)',
  'llm.skills': 'Juez de habilidades por turno en charla libre (speak, sin tools)',
  'llm.debrief': 'Post-sesión · juez de la misión de escenarios y capstones (speak)',
  'llm.translate': 'Traducción de líneas del coach (pipeline + on-demand)',
  'llm.explain': 'Explainer de correcciones (Anthropic-only, forced tool)',
  'llm.suggest': 'Sugerencias de reply (Anthropic-only, forced tool)',
  'llm.scenario': 'Generador de escenarios speak/spar (Anthropic-only, forced tool)',
  'llm.lookup': 'Lookup de palabra/frase (diccionario + glosario)',
  'llm.meaning': 'Significado según la frase: elige el de la ficha (Jev o un modelo de chat)',
  'llm.example_judge': 'Juez del ejemplo corto de una palabra guardada (repaso, distinto del que escribe)',
  'llm.game': 'Reply en modo juego (plan 42)',
  'llm.game_check': 'Chequeo del intento en translate-it antes del reply (Jev)',
  'llm.game_solution': 'Solución de translate-it en pantalla («No sé» / «Ver solución»)',
  'llm.recall_select': 'Selector del recall en vivo: qué notas de memoria llegan al reply (Jev)',
  'llm.recall': 'Active recall (memoria)',
  'llm.reflect': 'Reflexión periódica (coach)',
  'llm.safety': 'Safety classifier (coach)',
  'llm.summarize': 'Resumen automático',
  'llm.theory': 'Chat del coach sobre la teoría (speak)',
  'llm.news_classify': 'Clasificador del News Service (costo operativo)',
  'llm.verify': 'Decisiones tipadas (Jev) · verificador de correcciones del side-runner',
  'video.analyze': 'Análisis Gemini de video',
  tts: 'Text-to-speech del bot (override mismo provider)',
  stt: 'Speech-to-text del user (override mismo provider)',
};

export function RoutingPage(): ReactElement {
  const [tab, setTab] = useState<'edit' | 'audit'>('edit');
  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Routing · Modelos LLM</h1>
          <p className="page-sub">
            Override global de provider/modelo por task. Catálogo central validado server-side.
          </p>
        </div>
      </div>
      <div className="config-page">
        <section className="card">
          <header className="card-head config-tabs">
            <div className="segmented" role="tablist" aria-label="Vista">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'edit'}
                className={tab === 'edit' ? 'is-active' : ''}
                onClick={() => setTab('edit')}
              >
                Editar
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'audit'}
                className={tab === 'audit' ? 'is-active' : ''}
                onClick={() => setTab('audit')}
              >
                Historial
              </button>
            </div>
            <small>
              Cambios afectan la <strong>próxima sesión</strong> · no mid-session
            </small>
          </header>
          {tab === 'edit' ? <RoutingEditor /> : <RoutingAudit />}
        </section>
      </div>
    </>
  );
}

function RoutingEditor(): ReactElement {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['admin', 'routing'],
    queryFn: fetchRoutingConfig,
  });
  const [draft, setDraft] = useState<RoutingOverrides | null>(null);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const effective = useMemo<RoutingOverrides>(
    () => draft ?? query.data?.overrides ?? {},
    [draft, query.data],
  );

  const mutation = useMutation({
    mutationFn: (next: RoutingOverrides) => patchRoutingConfig(next),
    onSuccess: () => {
      setDraft(null);
      setToast({ kind: 'ok', text: 'Routing guardado' });
      void qc.invalidateQueries({ queryKey: ['admin', 'routing'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'routing-audit'] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof AdminApiError ? err.message : String(err);
      setToast({ kind: 'err', text: msg });
    },
  });

  if (query.isPending) return <div className="config-state">Cargando…</div>;
  if (query.isError) {
    return (
      <div className="config-state config-state-err">
        Error: {(query.error).message}
      </div>
    );
  }
  if (!query.data) return <div className="config-state">Sin datos</div>;

  const data = query.data;
  const isDirty = draft !== null;

  const updateOverride = (task: TaskType, value: ModelOverride | null): void => {
    const next: RoutingOverrides = { ...effective };
    if (value === null) delete next[task];
    else next[task] = value;
    setDraft(next);
  };

  return (
    <div className="config-editor">
      <div className="config-meta">
        <span className="config-meta-label">Última edición</span>
        <span className="config-meta-value mono">
          {data.updatedAt
            ? new Date(data.updatedAt).toLocaleString('es-AR', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })
            : 'nunca · usando defaults del código'}
        </span>
      </div>
      <table className="data-table">
        <thead>
          <tr>
            <th>Task</th>
            <th>Default</th>
            <th>Override</th>
            <th aria-label="acción" />
          </tr>
        </thead>
        <tbody>
          {ROUTABLE_TASKS.map((task) => (
            <RoutingRow
              key={task}
              task={task}
              data={data}
              currentOverride={effective[task] ?? null}
              onChange={(v) => updateOverride(task, v)}
              disabled={mutation.isPending}
            />
          ))}
        </tbody>
      </table>
      <footer className="config-footer">
        <div className="config-footer-left">
          {isDirty && (
            <span className="config-dirty-pill">
              <span className="config-dirty-dot" aria-hidden="true" />
              Hay cambios sin guardar
            </span>
          )}
          {toast && (
            <span
              className={toast.kind === 'ok' ? 'config-toast ok' : 'config-toast err'}
              role="status"
            >
              {toast.text}
            </span>
          )}
        </div>
        <div className="config-footer-actions">
          {isDirty && (
            <button
              type="button"
              className="btn ghost"
              onClick={() => setDraft(null)}
              disabled={mutation.isPending}
            >
              Descartar
            </button>
          )}
          <button
            type="button"
            className="btn primary"
            disabled={!isDirty || mutation.isPending}
            onClick={() => draft && mutation.mutate(draft)}
          >
            {mutation.isPending ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </footer>
    </div>
  );
}

interface RoutingRowProps {
  task: TaskType;
  data: RoutingConfigResponse;
  currentOverride: ModelOverride | null;
  onChange: (value: ModelOverride | null) => void;
  disabled?: boolean;
}

function RoutingRow({
  task,
  data,
  currentOverride,
  onChange,
  disabled,
}: RoutingRowProps): ReactElement {
  const defaultRoute = data.defaults[task];
  const candidates = useMemo(
    () =>
      data.catalog.filter(
        (entry) => entry.supports.includes(task) && entry.status !== 'deprecated',
      ),
    [data.catalog, task],
  );
  const overrideKey = currentOverride ? `${currentOverride.provider}:${currentOverride.model}` : '';
  const overrideEntry = useMemo(
    () =>
      currentOverride
        ? data.catalog.find(
            (e) => e.id === currentOverride.model && e.provider === currentOverride.provider,
          ) ?? null
        : null,
    [data.catalog, currentOverride],
  );

  const handleChange = (value: string): void => {
    if (!value) {
      onChange(null);
      return;
    }
    const [provider, ...rest] = value.split(':');
    const model = rest.join(':');
    if (!provider || !model) return;
    onChange({ provider, model });
  };

  return (
    <tr>
      <td>
        <code className="mono">{task}</code>
        {TASK_DESCRIPTIONS[task] && (
          <div className="config-field-hint">{TASK_DESCRIPTIONS[task]}</div>
        )}
      </td>
      <td>
        <span className="mono">{describeRoute(defaultRoute)}</span>
      </td>
      <td>
        <select
          value={overrideKey}
          onChange={(e) => handleChange(e.target.value)}
          disabled={disabled}
          className="config-input"
        >
          <option value="">— (usar default)</option>
          {candidates.map((entry) => (
            <option key={`${entry.provider}:${entry.id}`} value={`${entry.provider}:${entry.id}`}>
              {entry.displayName}
              {entry.status === 'experimental' ? ' ⚠' : ''}
              {entry.tier === 'frontier' ? ' · frontier' : ''}
              {' · '}
              {formatPricing(entry.pricing)}
            </option>
          ))}
        </select>
        {overrideEntry?.status === 'experimental' && (
          <p className="config-field-hint">
            ⚠ Experimental · {overrideEntry.notes ?? 'validar antes en módulos no críticos'}
          </p>
        )}
      </td>
      <td>
        {currentOverride && (
          <button
            type="button"
            className="btn ghost"
            onClick={() => onChange(null)}
            disabled={disabled}
          >
            Reset
          </button>
        )}
      </td>
    </tr>
  );
}

function describeRoute(route: { provider: string; model: string } | undefined): string {
  if (!route) return '—';
  return `${route.provider}/${route.model}`;
}

function RoutingAudit(): ReactElement {
  const query = useQuery({
    queryKey: ['admin', 'routing-audit'],
    queryFn: fetchRoutingAudit,
  });
  if (query.isPending) return <div className="config-state">Cargando…</div>;
  if (query.isError) {
    return (
      <div className="config-state config-state-err">
        Error: {(query.error).message}
      </div>
    );
  }
  const entries = query.data?.entries ?? [];
  if (entries.length === 0) {
    return (
      <div className="config-state">
        Sin cambios registrados. El primer override va a aparecer acá.
      </div>
    );
  }
  return (
    <div className="config-audit">
      {entries.map((entry) => (
        <RoutingAuditCard key={entry.id} entry={entry} />
      ))}
    </div>
  );
}

function RoutingAuditCard({ entry }: { entry: RoutingAuditEntry }): ReactElement {
  const when = new Date(entry.changedAt).toLocaleString('es-AR', {
    dateStyle: 'medium',
    timeStyle: 'medium',
  });
  return (
    <article className="audit-entry">
      <header className="audit-entry-head">
        <span className="audit-entry-when">{when}</span>
        <span className="audit-entry-by mono">
          por <code>{entry.changedByUserId?.slice(0, 8) ?? '—'}</code>
        </span>
      </header>
      <div className="audit-entry-diff">
        <div className="audit-side">
          <span className="audit-side-label">Antes</span>
          <pre className="audit-json">
            {entry.beforeConfig ? JSON.stringify(entry.beforeConfig, null, 2) : '— (sin row previa)'}
          </pre>
        </div>
        <div className="audit-side">
          <span className="audit-side-label">Después</span>
          <pre className="audit-json">{JSON.stringify(entry.afterConfig, null, 2)}</pre>
        </div>
      </div>
    </article>
  );
}
